import { throttle } from 'lodash'
import {
  createContext,
  createRef,
  Dispatch,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Dimensions, Platform, StatusBar, useWindowDimensions } from 'react-native'

import { BottomSheetBackdrop, BottomSheetModal } from '@gorhom/bottom-sheet'
import { Player } from '@ppwr/poker'

import { BottomModalContentConfig } from '../../../components/ANewDesign/BottomModalContent/Content'
import { useStore } from '../../../models'
import { events } from '../../../models/analytics/analytics-events'
import { FromScreen } from '../../../models/game/game-sheets'
import { getRouteToLastVisitedTab } from '../../../navigation'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'expo-router'
import { SharedValue, useSharedValue } from 'react-native-reanimated'

// ***
// PK: THIS IS THE LEGACY BOTTOM MODAL MANAGER FOR THE GAME SCREEN
//
// AI: This file manages all bottom modals in the game screen (betting, show cards, observers, side pots, etc).
// It uses a single BottomSheetModal from @gorhom/bottom-sheet and a Zustand store to manage the content
// and state of the modal. This allows for a consistent and centralized way to handle all bottom modals in
// the game screen, without having to manage multiple modal states across different components.
// ***

const getSnapPointHeightAt = (height: number, percentage: number) => {
  const rounded = (height * percentage) / 100
  return Math.floor(rounded)
}

const SCREEN_height = Dimensions.get('screen').height
const STATUS_BAR_height = StatusBar.currentHeight || 24
const WINDOW_height = Dimensions.get('window').height
const BOTTOM_GAP = SCREEN_height - WINDOW_height + STATUS_BAR_height
const isWeb = Platform.OS === 'web'

export const gameSheetModalRef = createRef<BottomSheetModal>()

type SnapPoints = (number | string)[]

type BottomModalContextProps = {
  bottomModalContent?: BottomModalContentConfig
  bottomSheetModalRef: RefObject<BottomSheetModal | null>
  enableDismissOnClose: boolean
  closeBottomModal: () => void
  isBottomModalOpen: boolean
  //
  renderBackdrop: (props: React.ComponentProps<typeof BottomSheetBackdrop>) => React.JSX.Element | null
  //
  snapPoints: SnapPoints
  setSnapPoints: Dispatch<SetStateAction<SnapPoints>>
  //
  currentPosition?: number
  onChangePosition: (index: number) => void
  // // Handlers
  handleOpenObservers: () => void
  handleOpenChats: () => void
  handleLeaveGame: () => void
  handleBet: () => void
  handleThreeCardBet: () => void
  handlePlayerCellPress: (player: Player) => void
  handleWalkthroughAnswer: (correct: boolean) => void
  handleSidePotsPress: () => void
  handleOpenGameStartModal: () => void
  handleOpenCallOptions: () => void
  handleTerminate: () => void
  handleShowCards: () => void
  animatedPosition: SharedValue<number>
}

const GameBottomModalContext = createContext<BottomModalContextProps>({} as BottomModalContextProps)

export const GameBottomModalProvider = observer(
  ({
    children,
    fromScreen = 'game'
  }: PropsWithChildren<{
    fromScreen?: FromScreen
  }>) => {
    const router = useRouter()
    const {
      analytics: { setEvent },
      game: {
        leaveCurrentGame,
        setPlayerUserIdForTeacher,
        removePlayerFromGame,
        isPreviousToLastStep,
        setShowConfirmLoader,
        gameId,
        setShowSidePots,
        setShowChats,
        getFirstGameSheet,
        shiftGameSheets,
        goToNextStep,
        isResponseVisible,
        togleIsReponseVisible,
        setIsReponseVisible,
        isRaiseOrDealTraining,
        isRaiseOrFold,
        isDealTheFlop,
        setIsBetModalOpen,
        setIsCallOptionsModalOpen,
        setIsGameStartSheetOpen,
        userGameScheduleFullResponse,
        deleteUserGameSchedule,
        resetFirstGame
      },
      communication,
      communication: { playerAVStatus, clearShowCards },
      session,
      session: { isTeacher, user, deleteAccount, lastVisitedTab },
      analytics,
      api: { mutateRemoveInviteToScheduleWithHash }
    } = useStore()

    const animatedPosition = useSharedValue(0)

    const sheet = getFirstGameSheet()
    const sheetKind = useMemo(() => sheet?.kind, [sheet?.kind])
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const { height } = useWindowDimensions()

    const [bottomModalContent, setBottomModalContent] = useState<BottomModalContentConfig>()

    const [currentPosition, setCurrentPosition] = useState<number>()
    // TODO: not in use, this could be removed
    const [enableDismissOnClose, setEnableDismissOnClose] = useState(true)

    const defaultFirstSnapPoint = getSnapPointHeightAt(height, 35)
    const [snapPoints, setSnapPoints] = useState<(number | string)[]>([defaultFirstSnapPoint])

    const isSmallerDevice = height < 670
    const isBigDevice = height > 900
    const isMediumDevice = height <= 900 && height > 670
    const isBottomModalOpen = Boolean(gameSheetModalRef.current) && Boolean(bottomModalContent)

    const dealTheFlopSnapPoints = useMemo(
      () => [
        getSnapPointHeightAt(
          height,
          isSmallerDevice ? 30 : isMediumDevice ? (Platform.OS === 'ios' ? 27 : 25) : isBigDevice ? 22 : 25
        ),
        getSnapPointHeightAt(height, isSmallerDevice ? 51 : isMediumDevice ? 48 : isBigDevice ? 38 : 38)
      ],
      [height, isBigDevice, isMediumDevice, isSmallerDevice]
    )

    const RaiseOrFoldSnapPoints = useMemo(
      () => [
        getSnapPointHeightAt(
          height,
          isSmallerDevice ? 30 : isMediumDevice ? (Platform.OS === 'ios' ? 19 : 19) : isBigDevice ? 22 : 25
        ),
        getSnapPointHeightAt(
          height,
          isSmallerDevice
            ? Platform.OS === 'ios'
              ? 70
              : 76
            : isMediumDevice
              ? Platform.OS === 'ios'
                ? 55
                : 68
              : isBigDevice
                ? 48
                : 38
        )
      ],
      [height, isBigDevice, isMediumDevice, isSmallerDevice]
    )

    const hideBackdrop = useMemo(() => {
      if (isRaiseOrDealTraining) {
        return isResponseVisible
      }
      const hideBackdropIn = ['GameStart']
      if (bottomModalContent?.kind !== undefined && hideBackdropIn.includes(bottomModalContent.kind)) {
        return true
      }
      return false
    }, [bottomModalContent, isRaiseOrDealTraining, isResponseVisible])

    const renderBackdrop = useCallback(
      (props: React.ComponentProps<typeof BottomSheetBackdrop>) => {
        //
        if (hideBackdrop) {
          return null
        }
        // NOTE: android with opacity 0 disable the backdrop, so 0.1 should do the trick
        const defaultOpacity = 0.1
        const opacity = bottomModalContent?.kind === 'Confirm' ? 0.5 : defaultOpacity

        return <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={opacity} />
      },
      [bottomModalContent?.kind, hideBackdrop]
    )

    const openBottomModal = useCallback(
      (content: BottomModalContentConfig, snap = defaultFirstSnapPoint) => {
        setBottomModalContent(content)
        if (content.kind === 'Walkthrough') {
          if (isDealTheFlop) {
            setSnapPoints(dealTheFlopSnapPoints)
          } else if (isRaiseOrFold) {
            setSnapPoints(RaiseOrFoldSnapPoints)
          }
        } else if (content.kind === 'Chats') {
          const snapFifty = getSnapPointHeightAt(height, 50)
          setSnapPoints([snapFifty, height])
        } else {
          setSnapPoints([snap])
        }
        gameSheetModalRef.current?.present()
      },
      [defaultFirstSnapPoint, height, isDealTheFlop, isRaiseOrFold, dealTheFlopSnapPoints, RaiseOrFoldSnapPoints]
    )

    const closeBottomModal = useCallback(() => {
      setShowSidePots(false)
      gameSheetModalRef.current?.close()
      setBottomModalContent(undefined)
      shiftGameSheets()
      setIsReponseVisible(true)
      setIsBetModalOpen(false)
      setIsCallOptionsModalOpen(false)
      setIsGameStartSheetOpen(false)
    }, [
      setShowSidePots,
      shiftGameSheets,
      setIsReponseVisible,
      setIsCallOptionsModalOpen,
      setIsBetModalOpen,
      setIsGameStartSheetOpen
    ])

    useEffect(() => {
      if (isRaiseOrDealTraining && isBottomModalOpen) {
        gameSheetModalRef.current?.snapToIndex(isResponseVisible ? 1 : 0)
      }
    }, [isResponseVisible, isRaiseOrDealTraining, isBottomModalOpen])

    const onInputFocus = () => {
      //
    }

    const onInputBlur = () => {
      //
    }

    //TODO: we can use this to end the call
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const endCall = throttle(async () => {
      closeBottomModal()
      communication.setPlayerVideoCallStatus(false, false, false)
      communication.setLocalUserMutedStatus(false)
      communication.setUserTappedOnGoLive(false)
      await communication.endCall()
    }, 500)

    const removePlayer = async () => {
      try {
        await removePlayerFromGame()
        closeBottomModal()
      } catch (error) {
        console.error('Error in removing player from game')
      }
    }

    const handleCloseOption = async (leave: boolean) => {
      if (leave) {
        try {
          setShowConfirmLoader(true)
          await leaveCurrentGame()
        } catch (error) {
          console.warn('gotoLobby no-op error', error)
        } finally {
          router.replace(getRouteToLastVisitedTab(lastVisitedTab))
          setShowConfirmLoader(false)
        }
      }
      closeBottomModal()
    }

    const handleCloseMyFirstGameOption = async () => {
      resetFirstGame()
      setEvent('First Game Exited', {
        source: 'side menu'
      })

      router.replace(getRouteToLastVisitedTab(lastVisitedTab))

      closeBottomModal()
    }

    const handleTerminateOptions = async (leave: boolean) => {
      if (leave) {
        try {
          setShowConfirmLoader(true)
          const userId = user?._id
          if (gameId && userId) {
            await communication.terminateGame(gameId, userId)
          }
        } catch (error) {
          console.warn('gotoLobby no-op error', error)
        } finally {
          setShowConfirmLoader(false)
        }
      }
      closeBottomModal()
    }

    const handleOpenObservers = () =>
      openBottomModal({
        kind: 'Observers',
        props: {}
      })

    const handleOpenChats = () => {
      analytics.setEvent(events.homescreen.chat_screen_viewed)
      openBottomModal({
        kind: 'Chats',
        props: {
          // onClose: closeBottomModal,
          onInputFocus,
          onInputBlur
        }
      })
    }

    /**
     * Open a confirmation modal when user wants to leave a community game.
     *
     * The modal will have a subtext explaining that leaving now means the user
     * will miss the chance to play at this table. The user can observe when they
     * return or join another community table if there's one available.
     *
     * @returns {void}
     */
    const handleLeaveCommunityGame = () =>
      openBottomModal(
        {
          kind: 'Confirm',
          props: {
            text: 'Are you sure?',
            subtext:
              "Leaving now means you'll miss your chance to play at this table. You can observe when you return or join another community table if there's one available.",
            confirm: { text: 'Stay and play', onPress: () => handleCloseOption(false) },
            cancel: { text: 'Leave Table', onPress: () => handleCloseOption(true) }
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 40 : isWeb ? 12 : 33) + BOTTOM_GAP
      )

    const handleLeaveGame = () =>
      openBottomModal(
        {
          kind: 'Confirm',
          props: {
            text: 'Are you sure?',
            confirm: { text: 'Yes, leave this game', onPress: () => handleCloseOption(true) },
            cancel: { text: "No, I'll stay", onPress: () => handleCloseOption(false) }
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 36 : isWeb ? 26 : 33)
      )

    const handleLeaveFirstGame = () => {
      openBottomModal(
        {
          kind: 'Confirm',
          props: {
            text: 'Are you sure?',
            confirm: { text: 'Yes, leave this module', onPress: () => handleCloseMyFirstGameOption() },
            cancel: { text: "No, I'll stay", onPress: () => handleCloseOption(false) }
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 36 : isWeb ? 26 : 33)
      )
    }

    // const deleteUserAccount = async (value: boolean) => {
    //   if (value) {
    //     try {
    //       setShowConfirmLoader(true)
    //       const userId = user?._id
    //       if (userId) {
    //         await deleteAccount()
    //         drawerNavigation.navigate(RootRoutes.auth)
    //       }
    //     } catch (error) {
    //       console.warn('gotologin', error)
    //     } finally {
    //       setShowConfirmLoader(false)
    //     }
    //   }
    //   closeBottomModal()
    // }

    const handleRemoveInvitation = async (value: boolean) => {
      if (value) {
        if (userGameScheduleFullResponse?.hash) {
          try {
            await mutateRemoveInviteToScheduleWithHash({ hash: userGameScheduleFullResponse.hash })
            router.replace('/(root)/(tabs)')
          } catch (error) {
            console.log(error)
          }
        }
      }
      closeBottomModal()
    }
    const handleRemoveSchedule = async (value: boolean) => {
      if (value) {
        if (userGameScheduleFullResponse?.hash) {
          try {
            await deleteUserGameSchedule(userGameScheduleFullResponse.hash)
            router.navigate('/(root)/(tabs)/home')
          } catch (error) {
            console.log(error)
          }
        }
      }
      closeBottomModal()
    }

    const handleTerminate = () =>
      openBottomModal(
        {
          kind: 'Confirm',
          props: {
            text: 'Do you want to terminate the game?',
            confirm: { text: 'Yes', onPress: () => handleTerminateOptions(true) },
            cancel: { text: 'No', onPress: () => handleTerminateOptions(false) }
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 40 : 33)
      )

    const handlePlayerCellPress = (player: Player) => {
      player?.userId && setPlayerUserIdForTeacher(player.userId)

      const playerAVStatusCall = playerAVStatus(player)

      const localPlayerId = session.user?._id
      const isLocalPlayer = localPlayerId === player?.userId
      const isTeacherOpeningOtherPlayer = isTeacher && !isLocalPlayer
      const isOtherPlayerOnAVCall = isTeacherOpeningOtherPlayer && playerAVStatusCall?.isPlayerOnAudioCall

      //Teacher options for managing AV controls for normal players.
      openBottomModal(
        {
          kind: 'CallOptions',
          props: {
            playerAVStatus: playerAVStatusCall,
            forPlayer: player,
            isTeacher,
            showPlayerControls: isOtherPlayerOnAVCall,
            isTeacherOpeningOtherPlayer,
            onClose: closeBottomModal,
            onRemovePlayer: removePlayer
          }
        },
        getSnapPointHeightAt(height, 30)
      )
    }

    const handleOpenCallOptions = () =>
      openBottomModal(
        {
          kind: 'CallOptions',
          props: {
            onClose: closeBottomModal,
            showPlayerControls: true
          }
        },
        getSnapPointHeightAt(height, 35)
      )

    const handleSidePotsPress = () => {
      setShowSidePots(true)
      openBottomModal(
        {
          kind: 'SidePots',
          props: {}
        },
        getSnapPointHeightAt(height, 42)
      )
    }

    const handleBet = () =>
      openBottomModal(
        {
          kind: 'Bet',
          props: {
            onClose: closeBottomModal
          }
        },
        getSnapPointHeightAt(
          height,
          isSmallerDevice
            ? Platform.OS === 'android'
              ? 86
              : 78
            : isMediumDevice
              ? Platform.OS === 'android'
                ? 75
                : 65
              : isBigDevice
                ? Platform.OS === 'android'
                  ? 58
                  : 60
                : 75
        )
      )

    const handleThreeCardBet = () =>
      openBottomModal(
        {
          kind: 'ThreeCardBet',
          props: {
            onClose: closeBottomModal
          }
        },
        getSnapPointHeightAt(height, 20)
      )

    const handleWalkthroughAnswer = (correct: boolean) =>
      openBottomModal(
        {
          kind: 'Walkthrough',
          props: {
            correct,
            onNext: () => {
              closeBottomModal()
              if (isPreviousToLastStep) {
                if (isRaiseOrFold) {
                  setEvent(events.homescreen.raise_or_fold_completed, { userId: user?._id.toString() })
                } else {
                  setEvent(events.homescreen.deal_the_flop_completed, { userId: user?._id.toString() })
                }
              }
              goToNextStep()
            },
            onViewTableOrResponse: () => {
              togleIsReponseVisible()
            }
          }
        },
        getSnapPointHeightAt(height, 70)
      )

    const handleOpenGameStartModal = () => {
      setIsGameStartSheetOpen(true)
      openBottomModal(
        {
          kind: 'GameStart',
          props: {
            onClose: closeBottomModal
          }
        },
        getSnapPointHeightAt(height, 25)
      )
    }

    // const handleDeleteAccountModal = () =>
    //   openBottomModal(
    //     {
    //       kind: 'Confirm',
    //       props: {
    //         text: 'Are you sure you want to delete your account?',
    //         confirm: { text: 'Yes', onPress: () => deleteUserAccount(true) },
    //         cancel: { text: 'No', onPress: () => deleteUserAccount(false) }
    //       }
    //     },
    //     getSnapPointHeightAt(height, isSmallerDevice ? 40 : 35)
    //   )

    const handleShowCards = () =>
      openBottomModal(
        {
          kind: 'ShowCards',
          props: {
            onClose: closeBottomModal,
            timeToKeepOpen: sheet?.timeToKeepOpen ?? 0
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 40 : 35)
      )

    const handleDeleteInviteModal = () =>
      openBottomModal(
        {
          kind: 'Confirm',
          props: {
            text: 'Are you sure?',
            confirm: { text: 'Remove Game', onPress: () => handleRemoveInvitation(true) },
            cancel: { text: 'Cancel', onPress: () => handleRemoveInvitation(false) }
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 40 : 35)
      )

    const handleDeleteScheduleModal = () =>
      openBottomModal(
        {
          kind: 'Confirm',
          props: {
            text: 'Are you sure?',
            subtext: 'The game will be removed from the "Your Games" section for you and everyone invited.',
            confirm: { text: 'Delete game for all', onPress: () => handleRemoveSchedule(true) },
            cancel: { text: 'Cancel', onPress: () => handleRemoveSchedule(false) }
          }
        },
        getSnapPointHeightAt(height, isSmallerDevice ? 40 : 35)
      )

    const onChangePosition = useCallback(
      (index: number) => {
        setCurrentPosition(index)

        if (isRaiseOrDealTraining) {
          setIsReponseVisible(index === 1)
        }
      },
      [isRaiseOrDealTraining, setIsReponseVisible]
    )

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (!sheet) {
        return
      }
      if (sheet?.timeToKeepOpen) {
        // @ts-expect-error
        timerRef.current = setTimeout(() => {
          closeBottomModal()
        }, sheet?.timeToKeepOpen * 1000)
      }

      switch (sheet?.kind) {
        case 'leaveCommunityGame':
          handleLeaveCommunityGame()
          break
        case 'leaveGame':
          handleLeaveGame()
          break
        case 'leaveFirstGame':
          handleLeaveFirstGame()
          break
        case 'observers':
          handleOpenObservers()
          break
        case 'terminateGame':
          handleTerminate()
          break
        // case 'deleteAccount':
        //   handleDeleteAccountModal()
        //   break
        case 'deleteInvite':
          handleDeleteInviteModal()
          break
        case 'deleteSchedule':
          handleDeleteScheduleModal()
          break
        case 'showCards':
          handleShowCards()
          break
        default:
          break
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }
      }
    }, [sheet])

    const ctx = {
      bottomModalContent,
      bottomSheetModalRef: gameSheetModalRef,
      enableDismissOnClose,
      closeBottomModal,
      isBottomModalOpen,
      //
      renderBackdrop,
      //
      snapPoints,
      setSnapPoints,
      currentPosition,
      onChangePosition,
      // Handlers
      handleOpenObservers,
      handleOpenChats,
      handleLeaveGame,
      handleBet,
      handleThreeCardBet,
      handlePlayerCellPress,
      handleWalkthroughAnswer,
      handleSidePotsPress,
      handleOpenGameStartModal,
      handleOpenCallOptions,
      handleTerminate,
      handleShowCards,
      animatedPosition
    }
    return <GameBottomModalContext.Provider value={ctx}>{children}</GameBottomModalContext.Provider>
  }
)

export const useGameBottomModal = () => {
  const ctx = useContext(GameBottomModalContext)

  if (!ctx) {
    throw new Error('useGameBottomModal needs to be used inside GameBottomModalProvider!')
  }

  return ctx
}
